import { 
  collection, 
  addDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from './firebase/config';

/**
 * Service de gestion des logs système
 * Enregistre les événements techniques et d'authentification dans Firestore
 * pour monitoring admin sans spam utilisateur
 */
class SystemLogService {
  constructor() {
    this.collectionName = 'system_logs';
  }

  /**
   * Enregistrer un événement système
   * @param {string} userId - ID de l'utilisateur concerné
   * @param {string} userEmail - Email de l'utilisateur (pour faciliter le debug admin)
   * @param {string} displayName - Nom d'affichage de l'utilisateur (optionnel)
   * @param {Object} logData - Données du log
   * @param {string} logData.type - Type de log ('auth', 'error', 'system', 'token', 'onedrive')
   * @param {string} logData.severity - Sévérité ('info', 'warning', 'error', 'success')
   * @param {string} logData.message - Message descriptif
   * @param {Object} logData.details - Détails additionnels (optionnel)
   * @returns {Promise<string>} ID du log créé
   */
  async logSystemEvent(userId, userEmail, displayName, logData) {
    try {
      const logEntry = {
        userId,
        userEmail: userEmail || 'unknown@unknown.com',
        displayName: displayName || 'Utilisateur',
        type: logData.type || 'system',
        severity: logData.severity || 'info',
        message: logData.message,
        details: logData.details || {},
        timestamp: serverTimestamp(),
        userAgent: navigator.userAgent || 'unknown',
        platform: navigator.platform || 'unknown',
        url: window.location.href || 'unknown'
      };

      const docRef = await addDoc(collection(db, this.collectionName), logEntry);
      return docRef.id;
    } catch (error) {
      console.error('[SystemLogService] Erreur lors de l\'enregistrement du log:', error);
      // On ne lance pas d'erreur pour éviter de casser l'app si les logs échouent
      return null;
    }
  }

  /**
   * Méthodes spécialisées pour les différents types de logs
   */

  // Logs d'authentification
  async logAuth(userId, userEmail, displayName, message, severity = 'info', details = {}) {
    return this.logSystemEvent(userId, userEmail, displayName, {
      type: 'auth',
      severity,
      message,
      details
    });
  }

  // Logs de tokens
  async logToken(userId, userEmail, displayName, message, severity = 'success', details = {}) {
    return this.logSystemEvent(userId, userEmail, displayName, {
      type: 'token',
      severity,
      message,
      details
    });
  }

  // Logs d'erreurs système
  async logError(userId, userEmail, displayName, message, error = null, details = {}) {
    return this.logSystemEvent(userId, userEmail, displayName, {
      type: 'error',
      severity: 'error',
      message,
      details: {
        ...details,
        error: error ? {
          name: error.name,
          message: error.message,
          stack: error.stack
        } : null
      }
    });
  }

  // Logs OneDrive
  async logOneDrive(userId, userEmail, displayName, message, severity = 'info', details = {}) {
    return this.logSystemEvent(userId, userEmail, displayName, {
      type: 'onedrive',
      severity,
      message,
      details
    });
  }

  // Logs système général
  async logSystem(userId, userEmail, displayName, message, severity = 'info', details = {}) {
    return this.logSystemEvent(userId, userEmail, displayName, {
      type: 'system',
      severity,
      message,
      details
    });
  }

  /**
   * Récupérer les logs système (admin uniquement côté client)
   * @param {Object} filters - Filtres optionnels
   * @param {string} filters.userId - Filtrer par utilisateur
   * @param {string} filters.type - Filtrer par type
   * @param {string} filters.severity - Filtrer par sévérité
   * @param {number} filters.limitResults - Nombre de résultats (défaut: 100)
   * @returns {Promise<Array>} Liste des logs
   */
  async getSystemLogs(filters = {}) {
    try {
      let q = collection(db, this.collectionName);
      
      // Application des filtres
      const constraints = [];
      
      if (filters.userId) {
        constraints.push(where('userId', '==', filters.userId));
      }
      if (filters.type) {
        constraints.push(where('type', '==', filters.type));
      }
      if (filters.severity) {
        constraints.push(where('severity', '==', filters.severity));
      }
      
      // Toujours ordonner par timestamp décroissant
      constraints.push(orderBy('timestamp', 'desc'));
      
      // Limite par défaut
      constraints.push(limit(filters.limitResults || 100));
      
      if (constraints.length > 0) {
        q = query(q, ...constraints);
      }

      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        // Convertir les timestamps Firestore en dates JavaScript
        timestamp: doc.data().timestamp?.toDate()
      }));
    } catch (error) {
      console.error('[SystemLogService] Erreur lors de la récupération des logs:', error);
      throw new Error('Impossible de récupérer les logs système');
    }
  }

  /**
   * Récupérer les logs d'un utilisateur spécifique
   * @param {string} userId - ID de l'utilisateur
   * @param {number} limitResults - Nombre de résultats (défaut: 50)
   * @returns {Promise<Array>} Liste des logs de l'utilisateur
   */
  async getUserLogs(userId, limitResults = 50) {
    return this.getSystemLogs({ userId, limitResults });
  }
}

// Instance singleton
const systemLogService = new SystemLogService();
export default systemLogService;